import type {
  QuestionWithSlide as EditorQuestionWithSlide,
  Slide as EditorSlide,
} from '@mentimeter/editor-schema/api-types-overrides';
import type { Question } from '@mentimeter/http-clients';
import { isProxyInstance } from '../../../utils/is-proxy-instance';
import { getCommonSlideCompatibilityHandler } from '../../common/get-common-slide-compatibility-handler';
import {
  editorSlidePropertyKeys,
  editorQuestionPropertyKeys,
} from './constants';
import { mapEditorSlidePropertyToQuestionProperty } from './map-editor-slide-property-to-question-property';
import { mapEditorQuestionPropertyToSlideProperty } from './map-editor-question-property-to-slide-property';

type Key =
  | keyof EditorSlide
  | keyof Question
  | '__isProxy'
  | '__target'
  | 'isMigrated';

const getEditorSlideCompatibilityHandler = (): ProxyHandler<
  EditorSlide | Question
> => {
  // Get the common compatibility handler
  const commonHandler = getCommonSlideCompatibilityHandler();

  return {
    get(target: EditorSlide | Question, key: Key, receiver: any) {
      const isTargetMigrated = Reflect.has(target, 'staticContent');
      // Handle editor specific slide properties
      if (editorSlidePropertyKeys.includes(key as keyof EditorSlide)) {
        const slide = target as EditorSlide;
        if (isTargetMigrated) {
          // data is already migrated, return the value directly
          // 'hasComments' -> 'hasComments'
          return slide[key as keyof typeof slide];
        }

        // data is not migrated map editor slide property to question property
        // 'hasComments' -> 'has_comments'
        return mapEditorSlidePropertyToQuestionProperty(
          target as Question,
          key as keyof EditorSlide,
        );
      }

      // Handle editor specific question properties
      if (editorQuestionPropertyKeys.includes(key as keyof Question)) {
        // Handle editor specific slide properties
        if (isTargetMigrated) {
          const slide = target as EditorSlide;
          // map has_comments -> hasComments
          return mapEditorQuestionPropertyToSlideProperty(
            slide,
            key as keyof Question,
          );
        }

        // data is not migrated yet, return the value directly
        // 'has_comments' -> 'has_comments'
        return (target as Question)[key as keyof Question];
      }

      // Fallback to the common compatibility handler
      return commonHandler.get
        ? commonHandler.get(target, key, receiver)
        : Reflect.get(target, key, receiver);
    },

    set(
      target: EditorSlide | Question,
      key: keyof EditorSlide | keyof Question,
      value: any,
      receiver: any,
    ) {
      return commonHandler.set
        ? commonHandler.set(target, key, value, receiver)
        : Reflect.set(target, key, value, receiver);
    },
  };
};

export function createEditorSlideCompatibilityLayer(
  target: Question | EditorSlide,
): EditorQuestionWithSlide {
  if (isProxyInstance(target)) {
    return target as EditorQuestionWithSlide;
  }

  return new Proxy(
    target,
    getEditorSlideCompatibilityHandler(),
  ) as EditorQuestionWithSlide;
}
